import type { PlatformChatApi } from '@liks/backend-svc-api/esm/apis/PlatformChatApi';
import type {
  PlatformChatKnowledgeBase,
  PlatformChatParticipant,
  PlatformChatSession,
} from '@liks/backend-svc-api/esm/models/index';
import type { PlatformChatSessionListItemWithIdentity } from '../types/chatSession';
import {
  hasId,
  hasUserId,
  isPlatformChatSessionListItemWithIdentity,
  toPlatformChatSessionListItemWithIdentity,
} from './platformChatServiceGuards';

export async function createSession(
  platformChatApi: PlatformChatApi,
  title: string,
): Promise<PlatformChatSession & { id: string }> {
  const created =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceCreatePlatformChatSession(
      {
        platformChatSession: {
          title: title.trim(),
        },
      },
    );

  if (!hasId(created)) {
    throw new Error('Backend returned a chat session without id');
  }

  return created;
}

export async function listPinnedSessions(
  platformChatApi: PlatformChatApi,
  options: {
    limit: number;
    offset: number;
    search?: string;
  },
): Promise<PlatformChatSessionListItemWithIdentity[]> {
  const page =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceListPinnedPlatformChatSessions(
      {
        limit: options.limit,
        offset: options.offset,
        ...(options.search != null && options.search.length > 0
          ? { search: options.search }
          : {}),
      },
    );

  return page
    .filter(isPlatformChatSessionListItemWithIdentity)
    .map(toPlatformChatSessionListItemWithIdentity);
}

export async function listUnpinnedSessions(
  platformChatApi: PlatformChatApi,
  options: {
    limit: number;
    offset: number;
    search?: string;
  },
): Promise<PlatformChatSessionListItemWithIdentity[]> {
  const page =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceListUnpinnedPlatformChatSessions(
      {
        limit: options.limit,
        offset: options.offset,
        ...(options.search != null && options.search.length > 0
          ? { search: options.search }
          : {}),
      },
    );

  return page
    .filter(isPlatformChatSessionListItemWithIdentity)
    .map(toPlatformChatSessionListItemWithIdentity);
}

export async function getSession(
  platformChatApi: PlatformChatApi,
  sessionId: string,
): Promise<(PlatformChatSession & { id: string }) | undefined> {
  try {
    const session =
      await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceReadPlatformChatSession(
        { sessionId },
      );
    return hasId(session) ? session : undefined;
  } catch {
    return undefined;
  }
}

export async function renameSession(
  platformChatApi: PlatformChatApi,
  session: PlatformChatSession,
  title: string,
): Promise<void> {
  if (!hasId(session)) {
    throw new Error('Cannot rename chat session without id');
  }

  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceUpdatePlatformChatSession(
    {
      sessionId: session.id,
      platformChatSession: {
        id: session.id,
        title: title.trim(),
        ownerUserId: session.ownerUserId,
        tenantId: session.tenantId,
      },
    },
  );
}

export async function deleteSession(
  platformChatApi: PlatformChatApi,
  sessionId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceDeletePlatformChatSession(
    { sessionId },
  );
}

export async function pinSession(
  platformChatApi: PlatformChatApi,
  sessionId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourcePinPlatformChatSession(
    { sessionId },
  );
}

export async function unpinSession(
  platformChatApi: PlatformChatApi,
  sessionId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceUnpinPlatformChatSession(
    { sessionId },
  );
}

export async function setSessionSystemPrompt(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  systemPrompt: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceConfigurePlatformChatSessionSystemPrompt(
    {
      sessionId,
      body: systemPrompt.trim(),
    },
  );
}

export async function listParticipants(
  platformChatApi: PlatformChatApi,
  sessionId: string,
): Promise<Array<PlatformChatParticipant & { id: string; userId: string }>> {
  const participants =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformChatParticipantResourceListSessionParticipants(
      { sessionId, limit: 1000 },
    );
  return participants.filter(
    (
      participant,
    ): participant is PlatformChatParticipant & {
      id: string;
      userId: string;
    } => hasId(participant) && hasUserId(participant),
  );
}

export async function inviteParticipant(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  userId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatParticipantResourceAddSessionParticipant(
    { sessionId, userId },
  );
}

export async function removeParticipant(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  participantId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatParticipantResourceRemoveSessionParticipant(
    { sessionId, id: participantId },
  );
}

export async function transferOwnership(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  newOwnerUserId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionResourceTransferPlatformChatOwnership(
    { sessionId, newOwnerUserId },
  );
}

export async function linkKnowledgeBase(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  knowledgeBaseId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatKnowledgeBaseResourceLinkKnowledgeBase(
    { sessionId, knowledgeBaseId },
  );
}

export async function listLinkedKnowledgeBases(
  platformChatApi: PlatformChatApi,
  sessionId: string,
): Promise<
  Array<PlatformChatKnowledgeBase & { id: string; knowledgeBaseId: string }>
> {
  const linkedKnowledgeBases =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformChatKnowledgeBaseResourceListLinkedKnowledgeBases(
      { sessionId, limit: 1000 },
    );

  return linkedKnowledgeBases.filter(
    (
      linkedKnowledgeBase,
    ): linkedKnowledgeBase is PlatformChatKnowledgeBase & {
      id: string;
      knowledgeBaseId: string;
    } =>
      hasId(linkedKnowledgeBase) &&
      typeof linkedKnowledgeBase.knowledgeBaseId === 'string' &&
      linkedKnowledgeBase.knowledgeBaseId.length > 0,
  );
}

export async function unlinkKnowledgeBase(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  linkId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatKnowledgeBaseResourceUnlinkKnowledgeBase(
    { sessionId, id: linkId },
  );
}
